import profileService from './profile.service';
import { GAMES } from '../../../mixins/games.commons';
import fortniteProfileService from './services/fortnite-profile.service';
import lolProfileService from './services/lol-profile.service';
import rocketLeagueProfileService from './services/rocket-league-profile.service';
import { PROFILE_STATS_COMPONENT_ACTIONS } from '../../../mixins/profile.commons';

// Game actions map
const gameActions = {
  [GAMES.LEAGUE_OF_LEGENDS]: 'getLolStats',
  [GAMES.FORTNITE]: 'getFortniteStats',
  [GAMES.ROCKET_LEAGUE]: 'getRocketLeagueStats',
};

const gameServices = {
  [GAMES.LEAGUE_OF_LEGENDS]: lolProfileService,
  [GAMES.FORTNITE]: fortniteProfileService,
  [GAMES.ROCKET_LEAGUE]: rocketLeagueProfileService,
};

export const statsActionMap = {
  [PROFILE_STATS_COMPONENT_ACTIONS.GENERAL_STATS]: 'getGeneralStats',
  [PROFILE_STATS_COMPONENT_ACTIONS.RANK]: 'getRankStats',
  [PROFILE_STATS_COMPONENT_ACTIONS.ACTIVITY]: 'getActivityStats',
};

export const actions = {

  async getGameStats({ commit }, { game, boomAccountId, profileKey }) {
    commit('GET_GAME_STATS', { game, profileKey });
    if (!gameActions[game]) {
      return commit('GET_GAME_STATS_FAILURE', { game, error: 'invalid_game', profileKey });
    }

    try {
      const { gameStats, actionNameErrors } = await gameServices[game].getProfileData(boomAccountId);
      commit('GET_GAME_STATS_SUCCESS', { game, gameStats, profileKey });

      if (actionNameErrors && actionNameErrors.length) {
        actionNameErrors.forEach(actionName => {
          commit('GET_PROFILE_COMPONENT_STATS_FAILURE', { error: 'server-error', game, actionName, profileKey });
        });
      }

      return gameStats;
    } catch (error) {
      commit('GET_GAME_STATS_FAILURE', { game, error, profileKey });
      throw error;
    }
  },

  async getConnectedGames({ commit, state, rootState }) {
    commit('GET_CONNECTED_GAMES');
    try {
      const gameList = await profileService.getConnectedGames(rootState.user.targetUser.name);
      // We check if the current selected game is connected.
      const selectedGameData = gameList.find(game => game.alias === state.selectedGame);
      if (!selectedGameData || !selectedGameData.connected) {
        // If not connected, we find the a connected game to set/show as selected in component and to request data to API.
        const firstConnectedGame = gameList.find(game => game.connected);
        if (firstConnectedGame) {
          commit('SELECT_GAME', { gameName: firstConnectedGame.alias });
        }
      }
      commit('GET_CONNECTED_GAMES_SUCCESS', { gameList });
      return;
    } catch (error) {
      commit('GET_CONNECTED_GAMES_FAILURE', { error });
    }
  },
  async updateConnectedGames({ commit, state }, listConnectedGames) {
    commit('GET_CONNECTED_GAMES');
    try {
      const gameList = await profileService.updateConnectedGames(listConnectedGames);
      // We check if the current selected game is connected.
      const selectedGameData = gameList.find(game => game.alias === state.selectedGame);
      if (!selectedGameData || !selectedGameData.connected) {
        // If not connected, we find the a connected game to set/show as selected in component and to request data to API.
        const firstConnectedGame = gameList.find(game => game.connected);
        if (firstConnectedGame) {
          commit('SELECT_GAME', { gameName: firstConnectedGame.alias });
        }
      }
      commit('GET_CONNECTED_GAMES_SUCCESS', { gameList });
      return;
    } catch (error) {
      commit('GET_CONNECTED_GAMES_FAILURE', { error });
    }
  },

  async getGeneralStats({ commit }, { game, boomAccountId, profileKey }) {
    commit('GET_GENERAL_STATS', { game, profileKey });
    try {
      const generalStats = await gameServices[game].getGeneralStats(boomAccountId);
      commit('GET_GENERAL_STATS_SUCCESS', { generalStats, game, profileKey });
    } catch (error) {
      commit('GET_GENERAL_STATS_FAILURE', { error, game, profileKey });
    }
  },

  async getRankStats({ commit }, { game, boomAccountId, profileKey }) {
    commit('GET_RANK_STATS', { game, profileKey });
    try {
      const rankStats = await gameServices[game].getRankStats(boomAccountId);
      commit('GET_RANK_STATS_SUCCESS', { rankStats, game, profileKey });
    } catch (error) {
      commit('GET_RANK_STATS_FAILURE', { error, game, profileKey });
    }
  },

  async getActivityStats({ commit }, { game, boomAccountId, profileKey }) {
    commit('GET_ACTIVITY_STATS', { game, profileKey });
    try {
      const activityStats = await gameServices[game].getActivityStats(boomAccountId);
      commit('GET_ACTIVITY_STATS_SUCCESS', { activityStats, game, profileKey });
    } catch (error) {
      commit('GET_ACTIVITY_STATS_FAILURE', { error, game, profileKey });
    }
  },

  async getProfileComponentStats({ commit }, { componentName, game, boomAccountId, profileKey }) {
    const actionName = statsActionMap[componentName];

    commit('GET_PROFILE_COMPONENT_STATS', { game, actionName, profileKey });
    try {
      // NOTE format example: fortnitePlayerService.getQueuesStats();
      const gameStats = await gameServices[game][actionName](boomAccountId);
      commit('GET_PROFILE_COMPONENT_STATS_SUCCESS', { gameStats, game, actionName, profileKey });
    } catch (error) {
      commit('GET_PROFILE_COMPONENT_STATS_FAILURE', { error, game, actionName, profileKey });
    }
  },

  async getStat({ commit }, { game, boomAccountId, profileKey, stat }) {
    const statActionName = statsActionMap[stat];

    commit('GET_STAT', { game, statActionName, profileKey });
    try {
      // NOTE format example: fortnitePlayerService.getQueuesStats();
      const stats = await gameServices[game][statActionName](boomAccountId);
      commit('GET_STAT_SUCCESS', { game, statActionName, stat, stats, profileKey });
    } catch (error) {
      commit('GET_STAT_FAILURE', { game, statActionName, error, profileKey });
    }
  },

  async getProfileGeneralData({ commit }, { profileKey, boomAccountId }) {
    commit('GET_PROFILE_GENERAL_DATA', { profileKey });
    try {
      const profileData = await profileService.getGeneralData({ boomAccountId });
      commit('GET_PROFILE_GENERAL_DATA_SUCCESS', { profileData, profileKey });
    } catch (error) {
      commit('GET_PROFILE_GENERAL_DATA_FAILURE', { error, profileKey });
    }
  },

  async getLeaderboard({ commit }, { boomAccountId, game, options }) {
    commit('GET_LEADERBOARD', { game, options });
    try {
      const { list, newOffset } = await profileService.getLeaderboard(options);
      const current = await profileService.getLeaderboardCurrentUserData({ boomAccountId, options });
      console.log(current);
      commit('GET_LEADERBOARD_SUCCESS', { game, list, current, newOffset });
    } catch (error) {
      commit('GET_LEADERBOARD_FAILURE', { game, error });
    }
  },

  async getMoreLeaderboard({ commit, getters }, { game, options }) {
    const offset = getters.getLeaderboardOffset;
    commit('GET_MORE_LEADERBOARD', { game });
    try {
      const { list, newOffset } = await profileService.getLeaderboard({ ...options, offset });
      commit('GET_MORE_LEADERBOARD_SUCCESS', { game, list, newOffset });
      return;
    } catch (error) {
      commit('GET_MORE_LEADERBOARD_FAILURE', { game, error });
      throw error;
    }
  },

  async searchPlayers({ commit }, { game, options }) {
    commit('SEARCH_PLAYERS', { game });
    try {
      const playerList = await profileService.searchPlayers(options);
      commit('SEARCH_PLAYERS_SUCCESS', { game, playerList });
    } catch (error) {
      commit('SEARCH_PLAYERS_FAILURE', { game, error });
    }
  },

  async setOpponentBoomId({ state, commit }, { boomAccountId }) {
    if (boomAccountId !== state.opponentBoomId) {
      commit('RESET_OPPONENT');
    }
    commit('SET_OPPONENT_BOOM_ID', { boomAccountId });
  },

  async getCompareLeaderboard({ commit }, { game }) {
    const options = {
      game,
      timeFrame: 'current',
      community: 'overall',
      leaderboardType: 'points',
      limit: 5,
    };
    if (game === 'rocket-league') {
      options['leaderboardType'] = 'shots';
    }
    commit('GET_COMPARE_LEADERBOARD', { game });
    try {
      const { list } = await profileService.getLeaderboard(options);
      commit('GET_COMPARE_LEADERBOARD_SUCCESS', { game, list });
    } catch (error) {
      commit('GET_COMPARE_LEADERBOARD_FAILURE', { game, error });
    }
  },

};
